import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card } from '../ui/Card';
import { LoadingSpinner } from '../LoadingSpinner';
import { formatDuration, formatTimeAgo } from '../../utils/format';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import PersonIcon from '@mui/icons-material/Person';
import CategoryIcon from '@mui/icons-material/Category';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

interface TopContentData {
  topArtists: Array<{
    artist_name: string;
    artist_id: string;
    play_count: number;
    total_time: number;
    unique_songs: number;
  }>;
  topSongs: Array<{
    id: string;
    title: string;
    artist_name: string;
    play_count: number;
    total_time: number;
  }>;
  topGenres: Array<{
    genre: string;
    play_count: number;
    total_time: number;
    unique_songs: number;
  }>;
}

interface TopContentStatsProps {
  period: '7d' | '30d' | '90d' | '1y';
}

export function TopContentStats({ period }: TopContentStatsProps) {
  const [activeTab, setActiveTab] = useState<'artists' | 'songs' | 'genres'>('artists');

  const { data, isLoading, error } = useQuery<TopContentData>({
    queryKey: ['analytics', 'top-content', period],
    queryFn: async () => {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/analytics/top-content?period=${period}&limit=10`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch top content data');
      }
      
      return response.json();
    },
    refetchInterval: 5 * 60 * 1000,
  });

  const renderTopArtists = () => {
    if (!data?.topArtists?.length) {
      return (
        <div className="text-center py-8 text-slate-grey">
          <PersonIcon className="mx-auto mb-2 text-4xl opacity-50" />
          <p>No artist data available for this period</p>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {data.topArtists.map((artist, index) => (
          <div
            key={artist.artist_id}
            className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
          >
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-naia-pink to-soft-violet rounded-full flex items-center justify-center text-white font-bold text-sm">
                {index + 1}
              </div>
              <div>
                <p className="font-semibold text-pearl-white">{artist.artist_name}</p>
                <p className="text-sm text-slate-grey">
                  {artist.unique_songs} songs • {formatDuration(artist.total_time)}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-semibold text-pearl-white">{artist.play_count}</p>
              <p className="text-xs text-slate-grey">plays</p>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderTopSongs = () => {
    if (!data?.topSongs?.length) {
      return (
        <div className="text-center py-8 text-slate-grey">
          <MusicNoteIcon className="mx-auto mb-2 text-4xl opacity-50" />
          <p>No song data available for this period</p>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {data.topSongs.map((song, index) => (
          <div
            key={song.id}
            className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors group cursor-pointer"
          >
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-cyber-teal to-electric-blue rounded-full flex items-center justify-center text-white font-bold text-sm">
                {index + 1}
              </div>
              <div>
                <p className="font-semibold text-pearl-white">{song.title}</p>
                <p className="text-sm text-slate-grey">
                  {song.artist_name} • {formatDuration(song.total_time)}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <div className="text-right">
                <p className="font-semibold text-pearl-white">{song.play_count}</p>
                <p className="text-xs text-slate-grey">plays</p>
              </div>
              <PlayArrowIcon className="text-cyber-teal opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderTopGenres = () => {
    if (!data?.topGenres?.length) {
      return (
        <div className="text-center py-8 text-slate-grey">
          <CategoryIcon className="mx-auto mb-2 text-4xl opacity-50" />
          <p>No genre data available for this period</p>
        </div>
      );
    }

    const totalPlays = data.topGenres.reduce((sum, genre) => sum + genre.play_count, 0);

    return (
      <div className="space-y-3">
        {data.topGenres.map((genre, index) => {
          const percentage = totalPlays > 0 ? Math.round((genre.play_count / totalPlays) * 100) : 0;
          
          return (
            <div
              key={genre.genre}
              className="p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-soft-violet to-electric-blue rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {index + 1}
                  </div>
                  <div>
                    <p className="font-semibold text-pearl-white">{genre.genre}</p>
                    <p className="text-sm text-slate-grey">
                      {genre.unique_songs} songs • {formatDuration(genre.total_time)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-pearl-white">{genre.play_count}</p>
                  <p className="text-xs text-slate-grey">{percentage}%</p>
                </div>
              </div>
              
              {/* Progress bar */}
              <div className="w-full bg-white/10 rounded-full h-2">
                <div
                  className="bg-gradient-to-r from-soft-violet to-electric-blue h-2 rounded-full transition-all duration-300"
                  style={{ width: `${percentage}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'artists':
        return renderTopArtists();
      case 'songs':
        return renderTopSongs();
      case 'genres':
        return renderTopGenres();
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <h3 className="text-xl font-semibold text-pearl-white mb-4">Top Content</h3>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <h3 className="text-xl font-semibold text-pearl-white mb-4">Top Content</h3>
        <div className="flex items-center justify-center h-64 text-slate-grey">
          <p>Error loading top content data</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <h3 className="text-xl font-semibold text-pearl-white mb-4">Top Content</h3>
      
      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-6 bg-white/5 rounded-lg p-1">
        <button
          onClick={() => setActiveTab('artists')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'artists'
              ? 'bg-naia-pink text-white'
              : 'text-slate-grey hover:text-pearl-white hover:bg-white/10'
          }`}
        >
          <PersonIcon className="mr-1 text-sm" />
          Artists
        </button>
        <button
          onClick={() => setActiveTab('songs')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'songs'
              ? 'bg-naia-pink text-white'
              : 'text-slate-grey hover:text-pearl-white hover:bg-white/10'
          }`}
        >
          <MusicNoteIcon className="mr-1 text-sm" />
          Songs
        </button>
        <button
          onClick={() => setActiveTab('genres')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'genres'
              ? 'bg-naia-pink text-white'
              : 'text-slate-grey hover:text-pearl-white hover:bg-white/10'
          }`}
        >
          <CategoryIcon className="mr-1 text-sm" />
          Genres
        </button>
      </div>

      {/* Content */}
      <div className="max-h-96 overflow-y-auto">
        {renderContent()}
      </div>

      {/* Summary */}
      {data && (
        <div className="mt-4 pt-4 border-t border-white/10">
          <div className="grid grid-cols-3 gap-4 text-center text-sm">
            <div>
              <p className="text-slate-grey">Artists</p>
              <p className="text-pearl-white font-semibold">
                {data.topArtists?.length || 0}
              </p>
            </div>
            <div>
              <p className="text-slate-grey">Songs</p>
              <p className="text-pearl-white font-semibold">
                {data.topSongs?.length || 0}
              </p>
            </div>
            <div>
              <p className="text-slate-grey">Genres</p>
              <p className="text-pearl-white font-semibold">
                {data.topGenres?.length || 0}
              </p>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
} 